import i18n from "i18next";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const getActiveLocale = (): string | undefined => i18n.language || undefined;

export const parseLocalDateValue = (
  value?: Date | string | null,
): Date | undefined => {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : new Date(value.getTime());
  }

  const normalized = value.trim();
  const match = DATE_ONLY_PATTERN.exec(normalized);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const toLocalDateInputValue = (
  value?: Date | string | null,
): string => {
  const parsed = parseLocalDateValue(value);
  if (!parsed) {
    return "";
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatLocalDateDisplay = (
  value?: Date | string | null,
): string => {
  const parsed = parseLocalDateValue(value);
  if (!parsed) {
    return typeof value === "string" ? value : "";
  }

  return new Intl.DateTimeFormat(getActiveLocale(), {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

export const formatLocalTimeDisplay = (
  value?: Date | string | null,
): string => {
  if (!value) {
    return "";
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(getActiveLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

export const hasMeaningfulTime = (
  value?: Date | string | null,
): boolean => {
  if (!value) {
    return false;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }

  return (
    parsed.getHours() !== 0 ||
    parsed.getMinutes() !== 0 ||
    parsed.getSeconds() !== 0 ||
    parsed.getMilliseconds() !== 0
  );
};

export const formatMeetingTimeRange = (
  startTime?: string | null,
  endTime?: string | null,
): string => {
  if (startTime && endTime) {
    return `${startTime} - ${endTime}`;
  }

  return startTime || endTime || "No time specified";
};
